/**
 * Candidate Form Component
 * Tabbed modal form for adding/editing candidates
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import jobService from '../../shared/services/jobService';
import {
  CANDIDATE_STAGES,
  PRIORITY_OPTIONS,
  CANDIDATE_SOURCES,
  RECRUITER_OPTIONS
} from '../utils/candidateColumns';
import './CandidateForm.css';

const FORM_TABS = [
  { id: 'personal', label: 'Personal Info', icon: '👤' },
  { id: 'job', label: 'Job Info', icon: '💼' },
  { id: 'pipeline', label: 'Pipeline', icon: '📊' },
  { id: 'additional', label: 'Additional', icon: '📝' }
];

function CandidateForm({ candidate, jobs, stores = [], onSave, onClose }) {
  const [activeTab, setActiveTab] = useState('personal');
  const isNewCandidate = !candidate;

  const [formData, setFormData] = useState({
    candidate_name: '',
    candidate_email: '',
    candidate_phone_number: '',
    candidate_source: '',
    referred_by: '',
    job_id: '',
    standard_job_title: '',
    hiring_manager: '',
    assigned_recruiter: '',
    candidate_stage: isNewCandidate ? 'New' : '',
    priority: '',
    store_id: '',
    phone_screen_date: '',
    submittal_date: '',
    hm_first_interview: '',
    hm_second_interview: '',
    market_visit_date: '',
    interviewers: '',
    notes: '',
    rejection_reason_id: '',
    rejection_reason_details: '',
    ta_follow_up_reminder_date: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [rejectionReasons, setRejectionReasons] = useState([]);

  // Load rejection reasons on mount
  useEffect(() => {
    loadRejectionReasons();
  }, []);

  const loadRejectionReasons = async () => {
    try {
      const response = await jobService.getRejectionReasons();
      if (response.success && Array.isArray(response.data)) {
        setRejectionReasons(response.data.filter(r => r.is_active));
      }
    } catch (err) {
      console.error('Failed to load rejection reasons:', err);
    }
  };

  useEffect(() => {
    if (candidate) {
      // Find the job to populate location
      const selectedJob = jobs.find(job => job.id === candidate.job_id);

      setFormData({
        candidate_name: candidate.candidate_name || '',
        candidate_email: candidate.candidate_email || '',
        candidate_phone_number: candidate.candidate_phone_number || '',
        candidate_source: candidate.candidate_source || '',
        referred_by: candidate.referred_by || '',
        job_id: candidate.job_id || '',
        standard_job_title: candidate.standard_job_title || '',
        hiring_manager: candidate.hiring_manager || '',
        assigned_recruiter: candidate.assigned_recruiter || '',
        candidate_stage: candidate.candidate_stage || '',
        priority: candidate.priority || '',
        store_id: selectedJob?.location || candidate.job_location || '',
        phone_screen_date: candidate.phone_screen_date || '',
        submittal_date: candidate.submittal_date || '',
        hm_first_interview: candidate.hm_first_interview || '',
        hm_second_interview: candidate.hm_second_interview || '',
        market_visit_date: candidate.market_visit_date || '',
        interviewers: candidate.interviewers || '',
        notes: candidate.notes || '',
        rejection_reason_id: candidate.rejection_reason_id || '',
        rejection_reason_details: candidate.rejection_reason_details || '',
        ta_follow_up_reminder_date: candidate.ta_follow_up_reminder_date || ''
      });
    }
  }, [candidate, jobs]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    if (field === 'candidate_source' && value !== 'Referral') {
      setFormData(prev => ({ ...prev, referred_by: '' }));
    }
  };

  const handleJobChange = (jobId) => {
    setFormData(prev => ({ ...prev, job_id: jobId }));

    if (jobId) {
      const selectedJob = jobs.find(job => job.id === parseInt(jobId));
      if (selectedJob) {
        setFormData(prev => ({
          ...prev,
          job_id: jobId,
          standard_job_title: selectedJob.job_title || '',
          hiring_manager: selectedJob.hiring_manager || '',
          store_id: selectedJob.location || ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        job_id: '',
        standard_job_title: '',
        hiring_manager: '',
        store_id: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.candidate_name?.trim()) {
      newErrors.candidate_name = 'Candidate name is required';
    }

    if (formData.candidate_email && !isValidEmail(formData.candidate_email)) {
      newErrors.candidate_email = 'Invalid email address';
    }

    if (formData.candidate_source === 'Referral' && !formData.referred_by?.trim()) {
      newErrors.referred_by = 'Referred By is required when Source is Referral';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      // Switch to the tab with errors
      if (errors.candidate_name || errors.candidate_email || errors.referred_by) {
        setActiveTab('personal');
      }
      return;
    }

    try {
      setSaving(true);

      const sanitizedData = { ...formData };
      const dateFields = [
        'phone_screen_date',
        'submittal_date',
        'hm_first_interview',
        'hm_second_interview',
        'market_visit_date',
        'ta_follow_up_reminder_date'
      ];

      dateFields.forEach(field => {
        if (sanitizedData[field] === '' || sanitizedData[field] === undefined) {
          sanitizedData[field] = null;
        }
      });

      // Convert rejection_reason_id to number or null
      if (sanitizedData.rejection_reason_id === '' || sanitizedData.rejection_reason_id === undefined) {
        sanitizedData.rejection_reason_id = null;
      } else {
        sanitizedData.rejection_reason_id = parseInt(sanitizedData.rejection_reason_id);
      }

      await onSave(sanitizedData);
    } catch (err) {
      setErrors({ submit: 'Failed to save candidate. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Group rejection reasons by type
  const rejectedReasons = rejectionReasons.filter(r => r.reason_type === 'rejected');
  const withdrawnReasons = rejectionReasons.filter(r => r.reason_type === 'withdrawn');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <div className="tab-content">
            <div className="form-row">
              <div className="form-group">
                <label>
                  Candidate Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.candidate_name}
                  onChange={(e) => handleChange('candidate_name', e.target.value)}
                  className={errors.candidate_name ? 'error' : ''}
                />
                {errors.candidate_name && (
                  <span className="error-message">{errors.candidate_name}</span>
                )}
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.candidate_email}
                  onChange={(e) => handleChange('candidate_email', e.target.value)}
                  className={errors.candidate_email ? 'error' : ''}
                />
                {errors.candidate_email && (
                  <span className="error-message">{errors.candidate_email}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={formData.candidate_phone_number}
                  onChange={(e) => handleChange('candidate_phone_number', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Source</label>
                <select
                  value={formData.candidate_source}
                  onChange={(e) => handleChange('candidate_source', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {CANDIDATE_SOURCES.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.candidate_source === 'Referral' && (
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Referred By <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.referred_by}
                    onChange={(e) => handleChange('referred_by', e.target.value)}
                    className={errors.referred_by ? 'error' : ''}
                  />
                  {errors.referred_by && (
                    <span className="error-message">{errors.referred_by}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'job':
        return (
          <div className="tab-content">
            <div className="form-row">
              <div className="form-group full-width">
                <label>Select Job</label>
                <select
                  value={formData.job_id}
                  onChange={(e) => handleJobChange(e.target.value)}
                >
                  <option value="">-- Select Job --</option>
                  {jobs.map(job => {
                    const storeName = stores.find(s => s.id === parseInt(job.location))?.store_name || job.location || 'No Location';
                    return (
                      <option key={job.id} value={job.id}>
                        {job.job_title} - {storeName}
                      </option>
                    );
                  })}
                </select>
                <span className="field-hint">
                  If you need to edit a Job, please use the <Link to="/ta/job-tracker" className="hint-link">Job Tracker</Link>
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  value={formData.standard_job_title}
                  readOnly
                  disabled
                  className="readonly-field"
                  title="Auto-populated from selected job"
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.store_id ? stores.find(s => s.id === parseInt(formData.store_id))?.store_name || '' : ''}
                  readOnly
                  disabled
                  className="readonly-field"
                  title="Auto-populated from selected job"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Hiring Manager</label>
                <input
                  type="text"
                  value={formData.hiring_manager}
                  readOnly
                  disabled
                  className="readonly-field"
                  title="Auto-populated from selected job"
                />
              </div>

              <div className="form-group">
                <label>Priority</label>
                <input
                  type="text"
                  value={formData.priority}
                  readOnly
                  disabled
                  className="readonly-field"
                  title="Auto-populated from selected job"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Assigned Recruiter</label>
                <select
                  value={formData.assigned_recruiter}
                  onChange={(e) => handleChange('assigned_recruiter', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {RECRUITER_OPTIONS.map(recruiter => (
                    <option key={recruiter} value={recruiter}>{recruiter}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 'pipeline':
        return (
          <div className="tab-content">
            <div className="form-row">
              <div className="form-group">
                <label>Candidate Stage</label>
                {isNewCandidate ? (
                  <input
                    type="text"
                    value="New"
                    readOnly
                    disabled
                    className="readonly-field"
                    title="New candidates start with 'New' stage"
                  />
                ) : (
                  <select
                    value={formData.candidate_stage}
                    onChange={(e) => handleChange('candidate_stage', e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {CANDIDATE_STAGES
                      .filter(stage => !['Rejected', 'Withdrawn'].includes(stage))
                      .map(stage => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                  </select>
                )}
              </div>

              {!isNewCandidate && formData.candidate_stage === 'Phone Screen' && (
                <div className="form-group">
                  <label>Phone Screen Date</label>
                  <input
                    type="date"
                    value={formData.phone_screen_date}
                    onChange={(e) => handleChange('phone_screen_date', e.target.value)}
                  />
                </div>
              )}

              {!isNewCandidate && formData.candidate_stage === 'Submitted to HM' && (
                <div className="form-group">
                  <label>Submittal Date</label>
                  <input
                    type="date"
                    value={formData.submittal_date}
                    onChange={(e) => handleChange('submittal_date', e.target.value)}
                  />
                </div>
              )}

              {!isNewCandidate && formData.candidate_stage === 'HM 1st Interview' && (
                <div className="form-group">
                  <label>HM 1st Interview</label>
                  <input
                    type="date"
                    value={formData.hm_first_interview}
                    onChange={(e) => handleChange('hm_first_interview', e.target.value)}
                  />
                </div>
              )}

              {!isNewCandidate && formData.candidate_stage === 'HM 2nd Interview' && (
                <div className="form-group">
                  <label>HM 2nd Interview</label>
                  <input
                    type="date"
                    value={formData.hm_second_interview}
                    onChange={(e) => handleChange('hm_second_interview', e.target.value)}
                  />
                </div>
              )}

              {!isNewCandidate && formData.candidate_stage === 'Market Visit' && (
                <div className="form-group">
                  <label>Market Visit Date</label>
                  <input
                    type="date"
                    value={formData.market_visit_date}
                    onChange={(e) => handleChange('market_visit_date', e.target.value)}
                  />
                </div>
              )}
            </div>

            {!isNewCandidate && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Interviewers</label>
                  <input
                    type="text"
                    value={formData.interviewers}
                    onChange={(e) => handleChange('interviewers', e.target.value)}
                    placeholder="Comma-separated list"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'additional':
        return (
          <div className="tab-content">
            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                />
              </div>
            </div>

            {!isNewCandidate && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rejection/Withdrawal Reason</label>
                    <select
                      value={formData.rejection_reason_id}
                      onChange={(e) => handleChange('rejection_reason_id', e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {rejectedReasons.length > 0 && (
                        <optgroup label="Rejection Reasons">
                          {rejectedReasons.map(reason => (
                            <option key={reason.id} value={reason.id}>
                              {reason.reason_name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {withdrawnReasons.length > 0 && (
                        <optgroup label="Withdrawal Reasons">
                          {withdrawnReasons.map(reason => (
                            <option key={reason.id} value={reason.id}>
                              {reason.reason_name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Follow-up Reminder</label>
                    <input
                      type="date"
                      value={formData.ta_follow_up_reminder_date}
                      onChange={(e) => handleChange('ta_follow_up_reminder_date', e.target.value)}
                    />
                  </div>
                </div>

                {formData.rejection_reason_id && (
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Rejection/Withdrawal Details</label>
                      <textarea
                        value={formData.rejection_reason_details}
                        onChange={(e) => handleChange('rejection_reason_details', e.target.value)}
                        rows={3}
                        placeholder="Additional details about the rejection or withdrawal..."
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content candidate-form-modal">
        <div className="modal-header">
          <h2>{candidate ? 'Edit Candidate' : 'Add New Candidate'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="form-tabs">
          {FORM_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`form-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="candidate-form">
          {errors.submit && (
            <div className="alert alert-error">{errors.submit}</div>
          )}

          {/* Tab Content */}
          <div className="form-tab-content">
            {renderTabContent()}
          </div>

          {/* Fixed Actions at Bottom */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Candidate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CandidateForm;
